import { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already dismissed
    const isDismissed = localStorage.getItem('pwa-install-dismissed');
    if (isDismissed) {
      setDismissed(true);
      return;
    }

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return;
    }

    // Listen for beforeinstallprompt (Android/Chrome)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Detect iOS Safari
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isInStandaloneMode = ('standalone' in window.navigator) && (window.navigator as any).standalone;
    
    if (isIOS && !isInStandaloneMode) {
      // Show iOS instructions after 3 seconds
      setTimeout(() => {
        setShowIOSInstructions(true);
      }, 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setShowInstallButton(false);
    }

    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowInstallButton(false);
    setShowIOSInstructions(false);
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  if (dismissed) return null;

  // Android/Chrome Install Button
  if (showInstallButton) {
    return (
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-50 animate-slide-up">
        <div className="bg-gradient-to-r from-[#0A0F1E] to-[#0D1425] border border-[#00FF94]/20 rounded-lg shadow-2xl p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-12 h-12 bg-[#00FF94]/10 rounded-lg flex items-center justify-center">
              <i className="ri-download-cloud-line text-2xl text-[#00FF94]"></i>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-semibold text-sm mb-1">
                تثبيت التطبيق
              </h3>
              <p className="text-[#94A3B8] text-xs mb-3">
                ثبّت طيبات على جهازك للوصول السريع
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleInstallClick}
                  className="flex-1 bg-[#00FF94] text-[#0A0F1E] px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#00FF94]/90 transition-all whitespace-nowrap"
                >
                  تثبيت الآن
                </button>
                <button
                  onClick={handleDismiss}
                  className="px-4 py-2 text-[#94A3B8] hover:text-white text-sm transition-colors whitespace-nowrap"
                >
                  لاحقاً
                </button>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 text-[#94A3B8] hover:text-white transition-colors"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // iOS Instructions
  if (showIOSInstructions) {
    return (
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-50 animate-slide-up">
        <div className="bg-gradient-to-r from-[#0A0F1E] to-[#0D1425] border border-[#00FF94]/20 rounded-lg shadow-2xl p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-12 h-12 bg-[#00FF94]/10 rounded-lg flex items-center justify-center">
              <i className="ri-apple-line text-2xl text-[#00FF94]"></i>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-semibold text-sm mb-1">
                أضف إلى الشاشة الرئيسية
              </h3>
              <p className="text-[#94A3B8] text-xs mb-2">
                لتثبيت التطبيق على iOS:
              </p>
              <ol className="text-[#94A3B8] text-xs space-y-1 mb-3 pr-4">
                <li>1. اضغط على زر المشاركة <i className="ri-share-line"></i></li>
                <li>2. اختر "أضف إلى الشاشة الرئيسية"</li>
                <li>3. اضغط "إضافة"</li>
              </ol>
              <button
                onClick={handleDismiss}
                className="w-full bg-[#00FF94]/10 text-[#00FF94] px-4 py-2 rounded-md text-sm font-semibold hover:bg-[#00FF94]/20 transition-all whitespace-nowrap"
              >
                فهمت
              </button>
            </div>
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 text-[#94A3B8] hover:text-white transition-colors"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
